import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getSocietyAdminData,
  getBankAccountSummary,
  getEnrichedPendingBillPayments,
  resolveDocumentImageUrl,
  revokeBlobUrl,
  isUnsignedGcsObjectUrl,
  approveBillPaymentsBatch,
  rejectBillPaymentsBatch,
  linkPaymentToBankLine,
  unlinkBill,
  ignoreTransaction,
  markPeriodAsSettled,
  parseStatementPdf,
  saveBankAccountSummary,
  BankAccountSummaryEntry,
  PendingPaymentApprovalGroup,
  ReconciliationMatch,
} from '../services/api';
import SocietyAdminReconcilePendingCard, {
  groupToSidebarCard,
  groupToReconRow,
  groupCardsByFlat,
  flatLabelFromCard,
  mergeGroupWithMatch,
  SidebarPendingCard,
  ReconPendingRow,
} from '../components/SocietyAdminReconcilePendingCard';
import {
  HiDocumentText,
  HiRefresh,
  HiExclamation,
  HiCheckCircle,
  HiBadgeCheck,
  HiX,
  HiUpload,
  HiSearch,
  HiEye,
  HiDotsVertical,
} from '../components/Icons';
import './SocietyAdminReconcile.css';

const ALL_TIME_START_DATE = '2000-01-01';

/** Map of ledgerEntryId → match info */
type MatchMap = Map<number, ReconciliationMatch & { _ledgerId: number }>;
/** Manual link: bankSummaryId → billPaymentId */
type ManualLinkMap = Map<number, number>;

const SocietyAdminReconcile: React.FC = () => {
  const navigate = useNavigate();
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;
  const societyName = adminData?.societyName || 'Society';

  const formatDescription = (desc: any) => {
    if (!desc) return '-';
    if (typeof desc !== 'string') return JSON.stringify(desc);
    try {
      // If it's a JSON string, parse and format it
      if (desc.startsWith('{') || desc.startsWith('[')) {
        const parsed = JSON.parse(desc);
        if (typeof parsed === 'object') {
          return Object.entries(parsed)
            .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
            .join(' | ');
        }
      }
      return desc;
    } catch (e) {
      return desc;
    }
  };

  // Data
  const [ledgerEntries, setLedgerEntries] = useState<BankAccountSummaryEntry[]>([]);
  const [loadingLedger, setLoadingLedger] = useState(true);
  const [ledgerSearch, setLedgerSearch] = useState('');

  const [pendingGroups, setPendingGroups] = useState<PendingPaymentApprovalGroup[]>([]);
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string>>({});
  const [loadingBills, setLoadingBills] = useState(true);
  const [billSearch, setBillSearch] = useState('');

  // Reconciliation
  const [hasRun, setHasRun] = useState(false);
  const [loadingReconcile, setLoadingReconcile] = useState(false);
  const [matchMap, setMatchMap] = useState<MatchMap>(new Map());
  const [manualLinks, setManualLinks] = useState<ManualLinkMap>(new Map());
  const [matchedBillIds, setMatchedBillIds] = useState<Set<number>>(new Set());

  // Approvals
  const [approvedBills, setApprovedBills] = useState<Set<number>>(new Set());
  const [approvingBills, setApprovingBills] = useState<Set<number>>(new Set());
  const [approveAllLoading, setApproveAllLoading] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type?: 'danger' | 'warning' | 'success';
  } | null>(null);

  // Manual linking
  const [manualLinkEntry, setManualLinkEntry] = useState<BankAccountSummaryEntry | null>(null);
  const [manualBillSearch, setManualBillSearch] = useState('');

  // Bill detail popup
  const [billPopup, setBillPopup] = useState<(ReconciliationMatch & { _ledgerId: number, needsReview?: boolean }) | null>(null);
  const [popupScreenshotUrl, setPopupScreenshotUrl] = useState<string | null>(null);
  const [popupScreenshotLoading, setPopupScreenshotLoading] = useState(false);
  const [isLinkingInPopup, setIsLinkingInPopup] = useState(false);
  const [popupTxnSearch, setPopupTxnSearch] = useState('');

  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);
  const [filterMode, setFilterMode] = useState<'all' | 'unmatched' | 'credits'>('all');
  const [showIgnored, setShowIgnored] = useState(false);
  const [showReconciled, setShowReconciled] = useState(false);
  const [expandedFlats, setExpandedFlats] = useState<Set<string>>(new Set());

  // Upload/Processing
  const location = useLocation();
  const [pdfFile, setPdfFile] = useState<File | null>((location.state as any)?.file || null);
  const [parsedTransactions, setParsedTransactions] = useState<any[]>([]);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [processingDone, setProcessingDone] = useState(false);

  const allTimeEndDate = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().slice(0, 10);
  }, []);

  const fmt = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const resolvePaymentIds = useCallback(
    (paymentId: number): number[] => {
      const group = pendingGroups.find((g) => g.payment_ids.includes(paymentId));
      if (group?.payment_ids.length) return group.payment_ids;
      const match = Array.from(matchMap.values()).find(
        (m) => m.paymentId === paymentId || m.billId === paymentId,
      );
      if (match?.paymentIds?.length) return match.paymentIds;
      return [paymentId];
    },
    [pendingGroups, matchMap],
  );

  // ─── Load Data ───
  const loadLedger = useCallback(async () => {
    if (!societyId) {
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoadingLedger(true);
    try {
      const res = await getBankAccountSummary(societyId, 1, 500);
      setLedgerEntries(res.data.entries);
    } catch (err: any) { setError(err.message || 'Failed to load ledger'); }
    finally { setLoadingLedger(false); }
  }, [societyId]);

  const loadBills = useCallback(async () => {
    setLoadingBills(true);
    try {
      const res = await getEnrichedPendingBillPayments(societyId);
      setPendingGroups(res.data);
      const urls: Record<string, string> = {};
      await Promise.all(
        res.data.map(async (g) => {
          if (!g.screenshot_file_path) return;
          try {
            urls[g.group_key] = await resolveDocumentImageUrl(g.screenshot_file_path);
          } catch {
            /* ignore missing screenshot */
          }
        }),
      );
      setScreenshotUrls(urls);
    } catch (err: any) { setError(err.message || 'Failed to load pending bills'); }
    finally { setLoadingBills(false); }
  }, [societyId]);

  const loadInitialData = useCallback(async () => {
    await Promise.all([loadLedger(), loadBills()]);
  }, [loadLedger, loadBills]);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);

  useEffect(() => {
    const screenshotFilePath =
      billPopup?.screenshotFilePath ??
      (billPopup
        ? pendingGroups.find((g) => g.payment_ids.includes(billPopup.paymentId))?.screenshot_file_path
        : null) ??
      null;

    if (!screenshotFilePath) {
      setPopupScreenshotUrl(null);
      setPopupScreenshotLoading(false);
      return;
    }

    const cachedGroup = pendingGroups.find((g) => g.payment_ids.includes(billPopup!.paymentId));
    const cachedUrl = cachedGroup ? screenshotUrls[cachedGroup.group_key] : null;
    if (cachedUrl && !isUnsignedGcsObjectUrl(cachedUrl)) {
      setPopupScreenshotUrl(cachedUrl);
      setPopupScreenshotLoading(false);
      return;
    }

    let cancelled = false;
    let blobUrl: string | null = null;
    setPopupScreenshotLoading(true);
    setPopupScreenshotUrl(null);
    resolveDocumentImageUrl(screenshotFilePath)
      .then((url) => {
        if (cancelled) {
          revokeBlobUrl(url);
          return;
        }
        blobUrl = url;
        setPopupScreenshotUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPopupScreenshotUrl(null);
      })
      .finally(() => {
        if (!cancelled) setPopupScreenshotLoading(false);
      });

    return () => {
      cancelled = true;
      revokeBlobUrl(blobUrl);
    };
  }, [billPopup, pendingGroups, screenshotUrls]);

  useEffect(() => {
    return () => {
      Object.values(screenshotUrls).forEach((url) => revokeBlobUrl(url));
    };
  }, [screenshotUrls]);

  // ─── PDF Parsing & Saving ───
  const parsePdf = useCallback(async () => {
    if (!pdfFile) return;
    setLoadingPdf(true);
    setError('');
    try {
      const res = await parseStatementPdf(pdfFile, societyId);
      setParsedTransactions(res.data.transactions);
    } catch (err: any) {
      setError(err.message || 'Failed to parse bank statement');
    } finally {
      setLoadingPdf(false);
    }
  }, [pdfFile]);

  useEffect(() => {
    if (pdfFile && parsedTransactions.length === 0 && !processingDone) {
      parsePdf();
    }
  }, [pdfFile, parsePdf, parsedTransactions.length, processingDone]);

  const handleSaveAndStart = async () => {
    if (!pdfFile || parsedTransactions.length === 0) return;
    setSavingSummary(true);
    setError('');
    try {
      // Saves parsed lines to bank_account_summary only (separate from society_transactions)
      await saveBankAccountSummary(societyId, {
        sourceFileName: pdfFile.name,
        bankAccountsSummary: parsedTransactions.map(txn => ({
          date: txn.date,
          particulars: txn.particulars,
          credit: txn.credit,
          debit: txn.debit,
          paymentMode: txn.paymentMode,
          transactionRefs: txn.transactionRefs,
        })),
      });
      
      setProcessingDone(true);
      setPdfFile(null);
      setParsedTransactions([]);
      await loadInitialData(); // Refresh everything to see new entries in ledger
      setSuccessMsg('Statement processed successfully! The actionable ledger has been updated.');
    } catch (err: any) {
      setError(err.message || 'Failed to save statement data');
    } finally {
      setSavingSummary(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setParsedTransactions([]);
      setProcessingDone(false);
    }
  };

  // ─── Filtered Ledger Entries ───
  const filteredLedger = useMemo(() => {
    return ledgerEntries.filter(entry => {
      // 1. Only show credit entries (inward payments)
      if (!(entry.credit !== null && entry.credit > 0)) return false;

      // 2. Hide already reconciled/approved entries (unless toggled)
      const isReconciled = entry.linkedBill && (entry.linkedBill.status === 'approved' || entry.linkedBill.status === 'reconciled');
      if (!showReconciled && isReconciled) return false;

      // 3. Basic Search
      if (ledgerSearch) {
        const q = ledgerSearch.toLowerCase();
        const searchStr = (entry.particulars || '').toLowerCase();
        const refMatch = entry.transactionRefs.some(r => r.toLowerCase().includes(q));
        const dateMatch = entry.transactionDate.includes(q);
        if (!searchStr.includes(q) && !refMatch && !dateMatch) return false;
      }

      // 4. Ignored filter
      if (!showIgnored && entry.isIgnored) return false;

      // 5. Mode filters (optional refinements)
      if (filterMode === 'unmatched') {
        const hasMatch = matchMap.has(entry.id);
        return !hasMatch;
      }
      return true; // 'all' or 'credits' (already handled by rule 1)
    });
  }, [ledgerEntries, ledgerSearch, filterMode, showIgnored, showReconciled, matchMap]);


  const isGroupMatched = useCallback(
    (g: PendingPaymentApprovalGroup) => g.payment_ids.some((id) => matchedBillIds.has(id)),
    [matchedBillIds],
  );

  const unmatchedGroups = useMemo(() => {
    let groups = pendingGroups.filter((g) => !isGroupMatched(g));
    if (billSearch) {
      const q = billSearch.toLowerCase();
      groups = groups.filter(
        (g) =>
          (g.flat_number || g.flat_label).toLowerCase().includes(q) ||
          (g.transaction_id || '').toLowerCase().includes(q) ||
          g.payment_ids.some((id) => String(id).includes(q)) ||
          String(g.total_amount_paid).includes(q) ||
          (g.block_name || '').toLowerCase().includes(q),
      );
    }
    return groups;
  }, [pendingGroups, isGroupMatched, billSearch]);

  const unmatchedRows = useMemo(
    () => unmatchedGroups.map(groupToReconRow),
    [unmatchedGroups],
  );

  const matchToCard = useCallback(
    (match: ReconciliationMatch & { _ledgerId: number }): SidebarPendingCard => {
      const group = pendingGroups.find((g) => g.payment_ids.includes(match.paymentId));
      if (group) {
        return {
          ...mergeGroupWithMatch(group, match),
          screenshotUrl: screenshotUrls[group.group_key] ?? null,
        };
      }
      const stub: PendingPaymentApprovalGroup = {
        group_key: `match-${match.paymentId}`,
        payment_ids: match.paymentIds ?? [match.paymentId],
        flat_id: 0,
        flat_label: match.flatNumber,
        block_name: match.blockName,
        flat_number: match.flatNumber,
        society_id: societyId,
        total_amount_paid: match.billAmount,
        payment_mode: match.paymentMode || '',
        transaction_id: match.billTransactionId,
        transaction_group_id: null,
        screenshot_file_path: match.screenshotFilePath,
        paid_at: match.billingDate,
        remarks: match.remarks,
        flat_summary: {
          total_outstanding: match.billAmount,
          total_principal: match.billAmount,
          total_interest: 0,
          total_advance: 0,
        },
        categories: [],
        payment_by_category: [],
        total_interest_waivable_max: 0,
      };
      return { ...groupToSidebarCard(stub), ...match, screenshotUrl: null };
    },
    [pendingGroups, screenshotUrls, societyId],
  );

  const sidebarCategories = useMemo(() => {
    const matched: SidebarPendingCard[] = [];
    const suspicious: SidebarPendingCard[] = [];
    const highRisk: SidebarPendingCard[] = [];

    if (hasRun) {
      matchMap.forEach((match) => {
        const card = matchToCard(match);
        if (match.matchReason.toLowerCase().includes('high risk')) highRisk.push(card);
        else if (match.matchConfidence === 'high') matched.push(card);
        else suspicious.push(card);
      });
    }

    const filterBySearch = (list: SidebarPendingCard[]) => {
      if (!billSearch) return list;
      const q = billSearch.toLowerCase();
      return list.filter(
        (b) =>
          (b.flatNumber || '').toLowerCase().includes(q) ||
          String(b.billId).includes(q) ||
          String(b.billAmount || b.total_amount_paid).includes(q) ||
          (b.matchReason || '').toLowerCase().includes(q) ||
          (b.transaction_id || b.transactionId || '').toLowerCase().includes(q),
      );
    };

    const unmatched = filterBySearch(
      unmatchedGroups.map((g) => ({
        ...groupToSidebarCard(g),
        screenshotUrl: screenshotUrls[g.group_key] ?? null,
      })),
    );

    return {
      unmatched,
      suspicious: filterBySearch(suspicious),
      matched: filterBySearch(matched),
      highRisk: filterBySearch(highRisk),
    };
  }, [hasRun, matchMap, unmatchedGroups, billSearch, matchToCard, screenshotUrls]);

  const openBillPopupFromCard = useCallback((card: SidebarPendingCard, needsReview?: boolean) => {
    const screenshotPath =
      card.screenshot_file_path ??
      (card as SidebarPendingCard & { screenshotFilePath?: string | null }).screenshotFilePath ??
      null;
    if (card.screenshotUrl && !isUnsignedGcsObjectUrl(card.screenshotUrl)) {
      setPopupScreenshotUrl(card.screenshotUrl);
      setPopupScreenshotLoading(false);
    } else {
      setPopupScreenshotUrl(null);
    }
    setBillPopup({
      billId: card.paymentId,
      paymentId: card.paymentId,
      paymentIds: card.payment_ids,
      needsReview,
      flatNumber: card.flatNumber,
      blockName: card.blockName,
      billAmount: card.total_amount_paid ?? card.billAmount,
      billingDate: card.paid_at,
      billTransactionId: card.transaction_id || card.transactionId || '',
      paymentMode: card.payment_mode || card.paymentMode,
      screenshotFilePath: screenshotPath,
      residentName: null,
      bankTransactionId: card.bankTransactionId || '',
      bankDate: card.bankDate || '',
      bankAmount: card.bankAmount || 0,
      bankParticulars: card.bankParticulars || '',
      bankPaymentMode: card.bankPaymentMode || null,
      matchConfidence: card.matchConfidence || 'low',
      matchReason: card.matchReason || 'Viewed from pending list',
      billType: card.categories[0]?.bill_type,
      remarks: card.remarks,
      _ledgerId: card._ledgerId || 0,
    });
  }, []);

  const isBillRowUsed = (bill: ReconPendingRow, used: Set<number>) =>
    bill.paymentIds.some((id) => used.has(id));

  const markBillRowUsed = (bill: ReconPendingRow, used: Set<number>) => {
    for (const id of bill.paymentIds) used.add(id);
  };

  const renderFlatGroupedCards = (
    cards: SidebarPendingCard[],
    opts: { isMatched?: boolean; isSuspicious?: boolean; isHighRisk?: boolean; isUnmatched?: boolean },
  ) =>
    groupCardsByFlat(cards, flatLabelFromCard).map(({ flatId, flatLabel, cards: flatCards }) => (
      <div key={`flat-${flatId}`} className="reconcile-flat-group">
        <div className="reconcile-flat-group-header">
          <span className="reconcile-flat-group-title">{flatLabel}</span>
          <span className="reconcile-flat-group-count">
            {flatCards.length} transaction{flatCards.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="reconcile-flat-group-cards">
          {flatCards.map((card) => renderPendingCard(card, opts))}
        </div>
      </div>
    ));

  const renderPendingCard = (
    card: SidebarPendingCard,
    opts: { isMatched?: boolean; isSuspicious?: boolean; isHighRisk?: boolean; isUnmatched?: boolean },
  ) => (
    <SocietyAdminReconcilePendingCard
      key={card.group_key || String(card.paymentId)}
      card={card}
      fmt={fmt}
      isMatched={opts.isMatched}
      isSuspicious={opts.isSuspicious}
      isHighRisk={opts.isHighRisk}
      isUnmatched={opts.isUnmatched}
      approved={approvedBills.has(card.paymentId)}
      approving={approvingBills.has(card.paymentId)}
      onViewDetails={() => openBillPopupFromCard(card)}
      onApprove={
        opts.isMatched
          ? () =>
              setConfirmModal({
                title: 'Approve Payment',
                message: `Approve payment for ${card.blockName ? `${card.blockName}-` : ''}${card.flatNumber}?`,
                onConfirm: () => handleApproveSingle(card.paymentId),
                type: 'success',
              })
          : () => openBillPopupFromCard(card, true)
      }
      onReject={() => handleRejectBill(card.paymentId)}
    />
  );

  const openTransactions = useMemo(() => {
    return ledgerEntries.filter(entry => {
      // 1. Must be a credit
      if (!(entry.credit !== null && entry.credit > 0)) return false;
      // 2. Not ignored or settled
      if (entry.isIgnored || entry.isSettled) return false;
      // 3. Not already linked/reconciled
      const isReconciled = entry.linkedBill && (entry.linkedBill.status === 'approved' || entry.linkedBill.status === 'reconciled');
      if (isReconciled) return false;
      // 4. Not matched in current session
      if (matchMap.has(entry.id)) return false;

      return true;
    });
  }, [ledgerEntries, matchMap]);


  // ─── Levenshtein Distance Helper ───
  const getLevenshteinDistance = (a: string, b: string): number => {
    const matrix = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 1; j <= b.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
      }
    }
    return matrix[a.length][b.length];
  };

  // ─── Run Reconciliation (Advanced 5-Pass) ───
  const handleRunReconcile = useCallback(() => {
    if (ledgerEntries.length === 0) { setError('No ledger entries. Upload a bank statement first.'); return; }
    
    setLoadingReconcile(true);
    setError('');
    setSuccessMsg('');
    setIsRightPanelOpen(true);

    const newMatchMap: MatchMap = new Map();
    const usedLedgerIds = new Set<number>();
    const usedBillIds = new Set<number>();

    // ── Step 0: Build set of ALL transaction IDs already consumed by linked/reconciled entries ──
    const consumedTxnIds = new Set<string>();
    for (const entry of ledgerEntries) {
      if (entry.linkedBill) {
        // This entry is already reconciled — collect all its transaction refs as "consumed"
        for (const ref of entry.transactionRefs) {
          consumedTxnIds.add(ref.toLowerCase().trim());
        }
        // Also mark these ledger entries as used so they can't be matched again
        usedLedgerIds.add(entry.id);
      }
    }

    // ── Step 1: Prepare pools (Exclude Settled/Ignored/Debits AND already-linked entries) ──
    const activeLedger = ledgerEntries.filter(e =>
      !e.isSettled && !e.isIgnored && e.credit !== null && e.credit > 0 && !e.linkedBill && !e.linkedPayment
    );
    const activeBills: ReconPendingRow[] = pendingGroups
      .filter((g) => !g.payment_ids.some((id) => matchedBillIds.has(id)))
      .map(groupToReconRow);

    // ── Step 2: Count duplicate transactionIds AMONG pending bills ──
    const pendingTxnIdCounts = new Map<string, number>();
    for (const bill of activeBills) {
      if (bill.transactionId) {
        const key = bill.transactionId.toLowerCase().trim();
        pendingTxnIdCounts.set(key, (pendingTxnIdCounts.get(key) || 0) + 1);
      }
    }

    // ── Step 3: Pre-flag High Risk bills ──
    const highRiskBills: ReconPendingRow[] = [];
    const cleanBills: ReconPendingRow[] = [];

    for (const bill of activeBills) {
      if (!bill.transactionId) {
        cleanBills.push(bill);
        continue;
      }

      const txnKey = bill.transactionId.toLowerCase().trim();
      const isConsumed = consumedTxnIds.has(txnKey);
      const isDuplicate = (pendingTxnIdCounts.get(txnKey) || 0) > 1;

      if (isConsumed || isDuplicate) {
        highRiskBills.push(bill);

        let reason = '';
        if (isConsumed && isDuplicate) {
          reason = `⚠️ High Risk: Transaction ID "${bill.transactionId}" is ALREADY used by a reconciled transaction AND shared by ${pendingTxnIdCounts.get(txnKey)} pending bills.`;
        } else if (isConsumed) {
          reason = `⚠️ High Risk: Transaction ID "${bill.transactionId}" is ALREADY used by a reconciled transaction. This bill needs admin review.`;
        } else {
          reason = `⚠️ High Risk: Transaction ID "${bill.transactionId}" is shared by ${pendingTxnIdCounts.get(txnKey)} pending bills. Only 1 bill can use this ID.`;
        }

        const virtualKey = -bill.paymentId;
        newMatchMap.set(virtualKey, {
          _ledgerId: virtualKey,
          paymentId: bill.paymentId,
          paymentIds: bill.paymentIds,
          billId: bill.paymentId,
          flatNumber: bill.flatNumber,
          blockName: bill.blockName,
          billAmount: bill.amount,
          billingDate: bill.billingDate || bill.paidAt,
          billTransactionId: bill.transactionId || '',
          paymentMode: bill.paymentMode,
          screenshotFilePath: bill.screenshotFilePath,
          residentName: bill.residentName,
          bankTransactionId: '',
          bankDate: '',
          bankAmount: 0,
          bankParticulars: '',
          bankPaymentMode: null,
          matchConfidence: 'low',
          matchReason: reason,
          billType: bill.billType,
          remarks: bill.remarks,
        });
        markBillRowUsed(bill, usedBillIds);
      } else {
        cleanBills.push(bill);
      }
    }

    // ── PASS 1: Exact Match (Ref + Amount) — only clean bills against unlinked ledger ──
    for (const bill of cleanBills) {
      if (isBillRowUsed(bill, usedBillIds) || !bill.transactionId) continue;
      const billTxnId = bill.transactionId.toLowerCase().trim();

      for (const entry of activeLedger) {
        if (usedLedgerIds.has(entry.id)) continue;
        const isExactRef = entry.transactionRefs.some(ref => ref.toLowerCase().trim() === billTxnId);
        const isExactAmt = Math.abs((entry.credit || 0) - bill.amount) < 0.01;

        if (isExactRef && isExactAmt) {
          newMatchMap.set(entry.id, {
            _ledgerId: entry.id,
            paymentId: bill.paymentId,
            paymentIds: bill.paymentIds,
            billId: bill.paymentId,
            flatNumber: bill.flatNumber,
            blockName: bill.blockName,
            billAmount: bill.amount,
            billingDate: bill.billingDate || bill.paidAt,
            billTransactionId: bill.transactionId,
            paymentMode: bill.paymentMode,
            screenshotFilePath: bill.screenshotFilePath,
            residentName: bill.residentName,
            bankTransactionId: entry.transactionRefs[0] || String(entry.id),
            bankDate: entry.transactionDate,
            bankAmount: entry.credit!,
            bankParticulars: entry.particulars,
            bankPaymentMode: entry.paymentMode,
            matchConfidence: 'high',
            matchReason: 'Exact reference & amount match',
            billType: bill.billType,
            remarks: bill.remarks,
          });
          usedLedgerIds.add(entry.id);
          markBillRowUsed(bill, usedBillIds);
          break;
        }
      }
    }

    // ── PASS 2: Fuzzy Ref Match (1 char diff) + Exact Amount ──
    for (const bill of cleanBills) {
      if (isBillRowUsed(bill, usedBillIds) || !bill.transactionId || bill.transactionId.length < 6) continue;
      const billTxnId = bill.transactionId.toLowerCase().trim();

      for (const entry of activeLedger) {
        if (usedLedgerIds.has(entry.id)) continue;
        const isExactAmt = Math.abs((entry.credit || 0) - bill.amount) < 0.01;
        if (!isExactAmt) continue;

        const isFuzzyRef = entry.transactionRefs.some(ref => {
          const r = ref.toLowerCase().trim();
          return r.length >= 6 && getLevenshteinDistance(r, billTxnId) === 1;
        });

        if (isFuzzyRef) {
          newMatchMap.set(entry.id, {
            _ledgerId: entry.id,
            paymentId: bill.paymentId,
            paymentIds: bill.paymentIds,
            billId: bill.paymentId,
            flatNumber: bill.flatNumber,
            blockName: bill.blockName,
            billAmount: bill.amount,
            billingDate: bill.billingDate || bill.paidAt,
            billTransactionId: bill.transactionId,
            paymentMode: bill.paymentMode,
            screenshotFilePath: bill.screenshotFilePath,
            residentName: bill.residentName,
            bankTransactionId: entry.transactionRefs[0] || String(entry.id),
            bankDate: entry.transactionDate,
            bankAmount: entry.credit!,
            bankParticulars: entry.particulars,
            bankPaymentMode: entry.paymentMode,
            matchConfidence: 'high',
            matchReason: 'Ref typo detected (Fuzzy match), amount exact',
            billType: bill.billType,
            remarks: bill.remarks,
          });
          usedLedgerIds.add(entry.id);
          markBillRowUsed(bill, usedBillIds);
          break;
        }
      }
    }

    // ── PASS 3: Partial Match (Ref Match, Amount Diff) ──
    for (const bill of cleanBills) {
      if (isBillRowUsed(bill, usedBillIds) || !bill.transactionId) continue;
      const billTxnId = bill.transactionId.toLowerCase().trim();

      for (const entry of activeLedger) {
        if (usedLedgerIds.has(entry.id)) continue;
        const isExactRef = entry.transactionRefs.some(ref => ref.toLowerCase().trim() === billTxnId);
        if (isExactRef) {
          newMatchMap.set(entry.id, {
            _ledgerId: entry.id,
            paymentId: bill.paymentId,
            paymentIds: bill.paymentIds,
            billId: bill.paymentId,
            flatNumber: bill.flatNumber,
            blockName: bill.blockName,
            billAmount: bill.amount,
            billingDate: bill.billingDate || bill.paidAt,
            billTransactionId: bill.transactionId,
            paymentMode: bill.paymentMode,
            screenshotFilePath: bill.screenshotFilePath,
            residentName: bill.residentName,
            bankTransactionId: entry.transactionRefs[0] || String(entry.id),
            bankDate: entry.transactionDate,
            bankAmount: entry.credit!,
            bankParticulars: entry.particulars,
            bankPaymentMode: entry.paymentMode,
            matchConfidence: 'low',
            matchReason: `⚠️ High Risk: Ref matched but amount differs (Bill: ${fmt(bill.amount)}, Bank: ${fmt(entry.credit!)})`,
            billType: bill.billType,
            remarks: bill.remarks,
          });
          usedLedgerIds.add(entry.id);
          markBillRowUsed(bill, usedBillIds);
          break;
        }
      }
    }

    setMatchMap(newMatchMap);
    setMatchedBillIds(usedBillIds);
    setHasRun(true);
    setLoadingReconcile(false);
    const hrCount = highRiskBills.length;
    setSuccessMsg(
      `Processed ${activeLedger.length} entries. Found ${newMatchMap.size - hrCount} matches` +
      (hrCount > 0 ? ` + ${hrCount} HIGH RISK bill(s) flagged for review.` : '.')
    );
  }, [ledgerEntries, pendingGroups, matchedBillIds, fmt]);


  const handleClearReconciliation = () => {
    setMatchMap(new Map());
    setMatchedBillIds(new Set());
    setHasRun(false);
    setSuccessMsg('Reconciliation results cleared.');
  };

  const handleCloseSidebar = () => {
    if (hasRun) {
      setConfirmModal({
        title: 'Clear Reconciliation?',
        message: 'Closing the Audit Sidebar will clear all potential matches. You will need to run reconciliation again to see them. Proceed?',
        onConfirm: () => {
          handleClearReconciliation();
          setIsRightPanelOpen(false);
        },
        type: 'warning',
        confirmText: 'Yes, Clear & Close'
      });
    } else {
      setIsRightPanelOpen(false);
    }
  };

  // ─── Reject Bill ───
  const handleRejectBill = async (billId: number) => {
    setConfirmModal({
      title: 'Reject Payment',
      message: 'Are you sure you want to reject this payment request? The resident will need to submit again.',
      onConfirm: async () => {
        try {
          await rejectBillPaymentsBatch(resolvePaymentIds(billId));
          setMatchMap(prev => {
            const next = new Map(prev);
            next.delete(-billId);
            Array.from(next.entries()).forEach(([key, val]) => {
              if (val.billId === billId) next.delete(key);
            });
            return next;
          });
          setMatchedBillIds(prev => {
            const next = new Set(prev);
            next.delete(billId);
            return next;
          });
          await loadInitialData();
          setSuccessMsg(`Payment #${billId} rejected and sent back to resident.`);
        } catch (err: any) {
          setError(err.message || 'Failed to reject bill');
        }
      },
      type: 'danger'
    });
  };

  // ─── Manual Link ───
  const handleManualLink = (ledgerId: number, bill: ReconPendingRow) => {
    const entry = ledgerEntries.find(e => e.id === ledgerId);
    if (!entry) return;

    const amountMatch = entry.credit !== null && Math.abs(entry.credit - bill.amount) < 0.01;

    const newMatch: ReconciliationMatch & { _ledgerId: number } = {
      _ledgerId: ledgerId,
      paymentId: bill.paymentId,
      paymentIds: bill.paymentIds,
      billId: bill.paymentId,
      flatNumber: bill.flatNumber,
      blockName: bill.blockName,
      billAmount: bill.amount,
      billingDate: bill.billingDate || bill.paidAt,
      billTransactionId: bill.transactionId || 'manual',
      paymentMode: bill.paymentMode,
      screenshotFilePath: bill.screenshotFilePath,
      residentName: bill.residentName,
      bankTransactionId: entry.transactionRefs[0] || String(entry.id),
      bankDate: entry.transactionDate,
      bankAmount: entry.credit!,
      bankParticulars: entry.particulars,
      bankPaymentMode: entry.paymentMode,
      matchConfidence: amountMatch ? 'high' : 'low',
      matchReason: 'Manually linked',
      billType: bill.billType,
      remarks: bill.remarks,
    };

    setMatchMap(prev => new Map(prev).set(ledgerId, newMatch));
    setMatchedBillIds((prev) => {
      const next = new Set(prev);
      for (const id of bill.paymentIds) next.add(id);
      return next;
    });
    setManualLinkEntry(null);
    setManualBillSearch('');
    setBillPopup(null);
    setIsLinkingInPopup(false);
    setPopupTxnSearch('');
  };

  const handleUnlink = async (ledgerId: number) => {
    try {
      await unlinkBill(ledgerId);
      await loadInitialData();
      setSuccessMsg('Successfully unlinked bill.');
    } catch (err: any) {
      setError(err.message || 'Failed to unlink');
    }
  };

  const handleIgnore = async (ledgerId: number, ignore: boolean) => {
    try {
      await ignoreTransaction(ledgerId, ignore);
      await loadLedger();
      setSuccessMsg(ignore ? 'Transaction ignored.' : 'Transaction restored.');
    } catch (err: any) {
      setError(err.message || 'Failed to ignore transaction');
    }
  };


  // ─── Approve ───
  const handleApproveSingle = async (billId: number) => {
    setApprovingBills(prev => new Set(prev).add(billId));
    try {
      const matchEntryId = Array.from(matchMap.entries()).find(([_, m]) => m.billId === billId)?.[0];
      const paymentIds = resolvePaymentIds(billId);
      if (matchEntryId) {
        await linkPaymentToBankLine(matchEntryId, billId);
      } else {
        await approveBillPaymentsBatch(paymentIds, {});
      }
      
      setApprovedBills(prev => new Set(prev).add(billId));
      await loadInitialData(); 
    } catch (err: any) { 
      setError(`Failed to approve payment #${billId}: ${err.message}`); 
    } finally { 
      setApprovingBills(prev => { const s = new Set(prev); s.delete(billId); return s; }); 
    }
  };
  const handleApproveBatch = async (matches: any[]) => {
    if (matches.length === 0) return;
    
    const hasSuspicious = matches.some(m => m.matchConfidence !== 'high');
    const msg = hasSuspicious 
      ? `⚠️ WARNING: This batch contains ${matches.filter(m => m.matchConfidence !== 'high').length} suspicious/low-confidence matches. \n\nAre you absolutely sure you want to bulk-approve all ${matches.length} bills?`
      : `Are you sure you want to approve and reconcile these ${matches.length} high-confidence matches?`;

    setConfirmModal({
      title: hasSuspicious ? '⚠️ Sensitive Batch Approval' : 'Batch Approval',
      message: msg,
      onConfirm: async () => {
        setApproveAllLoading(true);
        try {
          for (const match of matches) {
            if (approvedBills.has(match.billId)) continue;
            if (match._ledgerId > 0) {
              await linkPaymentToBankLine(match._ledgerId, match.billId);
            }
          }
          await loadInitialData();
          setSuccessMsg(`Successfully reconciled and approved ${matches.length} bills.`);
        } catch (err: any) { 
          setError(err.message || 'Batch approval failed'); 
        } finally { 
          setApproveAllLoading(false); 
        }
      },
      type: hasSuspicious ? 'warning' : 'success',
      confirmText: 'Approve All'
    });
  };

  const handleApproveAll = async () => {
    const highConfMatches = Array.from(matchMap.values())
      .filter(m => m.matchConfidence === 'high' && !approvedBills.has(m.billId));
    await handleApproveBatch(highConfMatches);
  };

  const [rejectAllLoading, setRejectAllLoading] = useState(false);

  const handleRejectBatch = async (bills: any[]) => {
    if (bills.length === 0) return;
    setConfirmModal({
      title: 'Bulk Rejection',
      message: `Are you sure you want to REJECT and send back all ${bills.length} high-risk bills to residents?`,
      onConfirm: async () => {
        setRejectAllLoading(true);
        try {
          for (const bill of bills) {
            await rejectBillPaymentsBatch(resolvePaymentIds(bill.paymentId ?? bill.billId));
          }
          await loadInitialData();
          setSuccessMsg(`Successfully rejected ${bills.length} bills.`);
        } catch (err: any) {
          setError(err.message || 'Batch rejection failed');
        } finally {
          setRejectAllLoading(false);
        }
      },
      type: 'danger',
      confirmText: 'Reject All'
    });
  };

  // ─── Stats ───
  const unapprovedHighCount = useMemo(() =>
    Array.from(matchMap.values()).filter(m => m.matchConfidence === 'high' && !approvedBills.has(m.billId)).length,
    [matchMap, approvedBills]
  );

  return (
    <div className={`reconcile-page ${isRightPanelOpen ? 'right-sidebar-open' : ''}`}>
      {/* ── Top Bar ── */}
      <div className="reconcile-top-bar" style={{ justifyContent: 'flex-end' }}>
        <div className="reconcile-top-actions">
          <button className="recon-btn recon-btn-outline" onClick={() => navigate('/society/ledger')}>
            ← Back to Ledger
          </button>
          

          {hasRun && unapprovedHighCount > 0 && (
            <button
              className="recon-btn recon-btn-success"
              onClick={handleApproveAll}
              disabled={approveAllLoading}
            >
              <HiCheckCircle size={16} />
              {approveAllLoading ? 'Approving...' : `Approve All High (${unapprovedHighCount})`}
            </button>
          )}

          <button
            className="recon-btn recon-btn-primary"
            onClick={handleRunReconcile}
            disabled={loadingReconcile || loadingLedger || loadingBills}
          >
            <HiRefresh className={loadingReconcile ? 'spin' : ''} size={16} />
            {loadingReconcile ? 'Matching...' : 'Run Reconciliation'}
          </button>
        </div>
      </div>


      {error && (
        <div className="recon-alert recon-alert-error">
          <HiExclamation size={18} /> {error}
          <button className="recon-alert-close" onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {successMsg && (
        <div className="recon-alert recon-alert-info">
          <HiCheckCircle size={18} /> {successMsg}
          <button className="recon-alert-close" onClick={() => setSuccessMsg('')}>&times;</button>
        </div>
      )}


      {/* ── Split Layout (always visible) ── */}
      <div className="reconcile-split-layout">
        {/* ▸ LEFT: Ledger Table (3/4) */}
        <div className="reconcile-left-panel">
          <div className="reconcile-panel-header">
            <h3><HiDocumentText size={18} /> Bank Ledger Summary</h3>
            
            <div className="filter-tabs">
              <button 
                className={`filter-tab ${filterMode === 'credits' ? 'active' : ''}`} 
                onClick={() => setFilterMode('credits')}
              >
                Credits Only
              </button>
              <button 
                className={`filter-tab ${filterMode === 'unmatched' ? 'active' : ''}`} 
                onClick={() => setFilterMode('unmatched')}
              >
                Unmatched
              </button>
              <button 
                className={`filter-tab ${filterMode === 'all' ? 'active' : ''}`} 
                onClick={() => setFilterMode('all')}
              >
                Full Ledger
              </button>
            </div>

            <label className="show-ignored-toggle">
              <input type="checkbox" checked={showIgnored} onChange={e => setShowIgnored(e.target.checked)} />
              Show Ignored
            </label>

            <label className="show-ignored-toggle" style={{ marginLeft: '15px' }}>
              <input type="checkbox" checked={showReconciled} onChange={e => setShowReconciled(e.target.checked)} />
              Show Reconciled
            </label>

            <div className="reconcile-search-box">
              <HiSearch size={14} />
              <input type="text" placeholder="Search ledger..." value={ledgerSearch} onChange={e => setLedgerSearch(e.target.value)} />
            </div>
          </div>
          <div className="reconcile-table-scroll">
            {loadingLedger ? (
              <div className="reconcile-loading"><HiRefresh size={20} /> Loading ledger...</div>
            ) : ledgerEntries.filter(e => e.credit !== null && e.credit > 0).length === 0 ? (
              <div className="reconcile-loading">No credit entries found in ledger.</div>
            ) : (
              <table className="recon-table reconcile-ledger-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Particulars</th>
                    <th>Credit</th>
                    <th>Debit</th>
                    <th>Refs</th>
                    <th>Payment #</th>
                    <th>Flat No.</th>
                    <th>Bill Amt</th>
                    <th className="col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLedger.map(entry => {
                    const match = matchMap.get(entry.id) || (entry.linkedBill ? ({
                      _ledgerId: entry.id,
                      billId: entry.linkedBill.billId,
                      flatNumber: entry.linkedBill.flatNumber,
                      blockName: entry.linkedBill.blockName,
                      billAmount: entry.linkedBill.amount,
                      billingDate: '', 
                      billTransactionId: '',
                      paymentMode: null,
                      screenshotFilePath: null,
                      residentName: entry.linkedBill.residentName,
                      bankTransactionId: entry.transactionRefs[0] || '',
                      bankDate: entry.transactionDate,
                      bankAmount: entry.credit || 0,
                      bankParticulars: entry.particulars,
                      bankPaymentMode: entry.paymentMode,
                      matchConfidence: 'high' as const,
                      matchReason: 'Persisted Link',
                      billType: entry.linkedBill.billType,
                      remarks: entry.linkedBill.remarks,
                    } as ReconciliationMatch & { _ledgerId: number }) : null);

                    const isLow = match && match.matchConfidence !== 'high';
                    const isApproved = (match && approvedBills.has(match.billId)) || (entry.linkedBill && entry.linkedBill.status === 'approved');
                    
                    const isHighRisk = match && match.matchReason.toLowerCase().includes('high risk');
                    let rowClass = '';
                    if (entry.isIgnored) rowClass = 'row-ignored';
                    else if (isApproved) rowClass = 'row-approved';
                    else if (isHighRisk) rowClass = 'row-high-risk';
                    else if (isLow) rowClass = 'row-suspicious';
                    else if (match) rowClass = 'row-matched';

                    return (
                      <React.Fragment key={entry.id}>
                        <tr className={rowClass}>
                          <td className="cell-mono">{entry.transactionDate}</td>
                          <td className="cell-particulars">
                            {entry.particulars || '-'}
                            {entry.isSettled && <span className="settled-tag">Settled</span>}
                          </td>
                          <td className="cell-amount cell-credit">{entry.credit !== null ? fmt(entry.credit) : '-'}</td>
                          <td className="cell-amount cell-debit">{entry.debit !== null ? fmt(entry.debit) : '-'}</td>
                          <td className="cell-mono cell-txn">
                            {entry.transactionRefs.length > 0 ? entry.transactionRefs.join(', ') : '-'}
                          </td>

                          <>
                            <td>
                              {match ? (
                                <button 
                                  className="cell-bill-id-btn" 
                                  onClick={() => setBillPopup({ ...match, _ledgerId: entry.id })}
                                  title="View Bill Details"
                                >
                                  #{match.billId}
                                </button>
                              ) : <span className="text-muted">—</span>}
                            </td>
                            <td className="cell-mono">
                              {match ? (
                                <span className="cell-flat-no">
                                  {match.blockName ? `${match.blockName}-` : ''}{match.flatNumber}
                                </span>
                              ) : <span className="text-muted">—</span>}
                            </td>
                            <td className="cell-amount">
                              {match ? (
                                <button className="match-amount-btn" onClick={() => setBillPopup({ ...match, _ledgerId: entry.id })}>
                                  {fmt(match.billAmount || 0)}
                                </button>
                              ) : <span className="text-muted">—</span>}
                            </td>
                          </>

                          <td className="col-action">
                            <div className="action-cell">
                              {match && !isApproved && (
                                <button
                                  className={`recon-btn recon-btn-sm ${isHighRisk ? 'recon-btn-danger' : (match.matchConfidence === 'high' ? 'recon-btn-success' : 'recon-btn-warning')}`}
                                  onClick={() => {
                                    if (match.matchConfidence === 'high') {
                                      setConfirmModal({
                                        title: 'Approve Payment',
                                        message: `Are you sure you want to approve Payment #${match.billId}?`,
                                        onConfirm: () => handleApproveSingle(match.billId),
                                        type: 'success'
                                      });
                                    } else {
                                      setBillPopup({ ...match, _ledgerId: entry.id, needsReview: true });
                                    }
                                  }}
                                  disabled={approvingBills.has(match.billId)}
                                  title="Approve Link"
                                >
                                  {approvingBills.has(match.billId) ? '...' : 'Approve'}
                                </button>
                              )}
                              
                              {match && isApproved && (
                                <span className="recon-badge badge-green"><HiCheckCircle size={12} /> Reconciled</span>
                              )}

                              <div className="dropdown-action-container">
                                <button 
                                  className="action-menu-trigger"
                                  onClick={() => setOpenActionMenuId(openActionMenuId === entry.id ? null : entry.id)}
                                >
                                  <HiDotsVertical size={16} />
                                </button>

                                {openActionMenuId === entry.id && (
                                  <>
                                    <div className="action-menu-overlay" onClick={() => setOpenActionMenuId(null)} />
                                    <div className="action-menu-dropdown">
                                      {match && (
                                        <button onClick={() => { setBillPopup({ ...match, _ledgerId: entry.id }); setOpenActionMenuId(null); }}>
                                          <HiEye size={14} /> View Details
                                        </button>
                                      )}
                                      
                                      {match && (
                                        <button 
                                          className="text-red" 
                                          onClick={() => { 
                                            setOpenActionMenuId(null);
                                            setConfirmModal({
                                              title: 'Unlink Transaction',
                                              message: `Are you sure you want to remove the link between this transaction and Payment #${match.billId}? This will mark the bill as UNPAID again.`,
                                              onConfirm: () => handleUnlink(entry.id),
                                              type: 'danger'
                                            });
                                          }}
                                        >
                                          <HiX size={14} /> Unlink Match
                                        </button>
                                      )}

                                      {!match && entry.credit !== null && entry.credit > 0 && !entry.isIgnored && !entry.isSettled && (
                                        <button onClick={() => { setManualLinkEntry(entry); setOpenActionMenuId(null); }}>
                                          + Link Bill
                                        </button>
                                      )}

                                      {!entry.isSettled && (
                                        <button onClick={() => { handleIgnore(entry.id, !entry.isIgnored); setOpenActionMenuId(null); }}>
                                          {entry.isIgnored ? 'Restore' : 'Ignore Entry'}
                                        </button>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="reconcile-panel-footer">
            {filteredLedger.length} entries shown
            {hasRun && <> · <strong>{matchMap.size}</strong> potential matches found</>}
            {' '}· Credit Total {fmt(filteredLedger.reduce((s, e) => s + (e.credit || 0), 0))}
          </div>
        </div>
      </div>

      {/* ▸ RIGHT: Pending Bills Sidebar */}
      <aside className={`reconcile-right-sidebar ${isRightPanelOpen ? 'open' : ''}`}>
        <div className="reconcile-panel-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <h3>{hasRun ? 'Audit Sidebar' : 'Pending Bills'}</h3>
            <span className="reconcile-panel-count">
              {hasRun ? (sidebarCategories.matched.length + sidebarCategories.suspicious.length + sidebarCategories.unmatched.length) : pendingGroups.length}
            </span>
          </div>
          <button className="close-sidebar-btn" onClick={handleCloseSidebar}>&times;</button>
        </div>
        <div className="reconcile-search-box reconcile-search-full">
          <HiSearch size={14} />
          <input type="text" placeholder="Search flat, name..." value={billSearch} onChange={e => setBillSearch(e.target.value)} />
        </div>
        <div className="reconcile-bills-scroll">
          {loadingBills ? (
            <div className="reconcile-loading"><HiRefresh size={20} /> Loading bills...</div>
          ) : (
            <div className="reconcile-bills-list">
              {/* --- HIGH RISK Section (always on top) --- */}
              {hasRun && sidebarCategories.highRisk.length > 0 && (
                <div className="sidebar-section">
                  <div className="sidebar-section-header high-risk">
                    <span>⚠️ High Risk — Action Required</span>
                    <div className="sidebar-header-actions">
                      <span className="section-count">{sidebarCategories.highRisk.length}</span>
                      <button 
                        className="recon-btn recon-btn-success recon-btn-sm recon-btn-xsmall"
                        onClick={() => handleRejectBatch(sidebarCategories.highRisk)}
                        disabled={rejectAllLoading}
                      >
                        Reject All
                      </button>
                    </div>
                  </div>
                  {renderFlatGroupedCards(sidebarCategories.highRisk, { isHighRisk: true })}
                </div>
              )}

              {/* --- Matched Section --- */}
              {hasRun && sidebarCategories.matched.length > 0 && (
                <div className="sidebar-section">
                  <div className="sidebar-section-header matched">
                    <span>Matched Bills</span>
                    <div className="sidebar-header-actions">
                      <span className="section-count">{sidebarCategories.matched.length}</span>
                      <button 
                        className="recon-btn recon-btn-success recon-btn-sm recon-btn-xsmall"
                        onClick={() => handleApproveBatch(sidebarCategories.matched)}
                        disabled={approveAllLoading}
                      >
                        Approve All
                      </button>
                    </div>
                  </div>
                  {renderFlatGroupedCards(sidebarCategories.matched, { isMatched: true })}
                </div>
              )}

              {/* --- Suspicious Section --- */}
              {hasRun && sidebarCategories.suspicious.length > 0 && (
                <div className="sidebar-section">
                  <div className="sidebar-section-header suspicious">
                    <span>Suspicious Matches</span>
                    <div className="sidebar-header-actions">
                      <span className="section-count">{sidebarCategories.suspicious.length}</span>
                      <button 
                        className="recon-btn recon-btn-warning recon-btn-sm recon-btn-xsmall"
                        onClick={() => handleApproveBatch(sidebarCategories.suspicious)}
                        disabled={approveAllLoading}
                      >
                        Approve All
                      </button>
                    </div>
                  </div>
                  {renderFlatGroupedCards(sidebarCategories.suspicious, { isSuspicious: true })}
                </div>
              )}

              {/* --- Unmatched Section --- */}
              <div className="sidebar-section">
                {hasRun && (
                  <div className="sidebar-section-header unmatched">
                    <span>{hasRun ? 'Unmatched Bills' : 'Pending Bills'}</span>
                    <div className="sidebar-header-actions">
                      <span className="section-count">{sidebarCategories.unmatched.length}</span>
                    </div>
                  </div>
                )}
                {sidebarCategories.unmatched.length === 0 && hasRun ? (
                  <div className="reconcile-loading small">🎉 All bills matched!</div>
                ) : (
                  renderFlatGroupedCards(sidebarCategories.unmatched, { isUnmatched: true })
                )}
              </div>
            </div>
          )}
        </div>
        <div className="sidebar-footer">
          <div className="sidebar-footer-stats">
            {hasRun
              ? `${unmatchedGroups.length} unmatched · ${fmt(unmatchedGroups.reduce((s, g) => s + g.total_amount_paid, 0))}`
              : `Total: ${fmt(pendingGroups.reduce((s, g) => s + g.total_amount_paid, 0))}`
            }
          </div>
          {hasRun && (
            <button 
              className="sidebar-clear-btn"
              onClick={handleClearReconciliation}
            >
              Clear Reconciliation Results
            </button>
          )}
        </div>
      </aside>

      {!isRightPanelOpen && (
        <button className="reconcile-right-sidebar-toggle" onClick={() => setIsRightPanelOpen(true)}>
          <HiDocumentText size={18} />
          <span>Pending Bills</span>
        </button>
      )}

      {/* ── Bill Detail Popup ── */}
      {billPopup && (
        <div className="bill-popup-overlay" onClick={() => setBillPopup(null)}>
          <div className="bill-popup" onClick={e => e.stopPropagation()}>
            <div className="bill-popup-header">
              <h3>Matched Bill Details</h3>
              <button className="recon-modal-close" onClick={() => setBillPopup(null)}>&times;</button>
            </div>
            <div className="bill-popup-body">
              <div className="bill-popup-confidence">
                <span className={`recon-badge ${billPopup.matchConfidence === 'high' ? 'badge-green' : 'badge-yellow'}`} style={{ fontSize: '0.85rem', padding: '5px 14px' }}>
                  {billPopup.matchConfidence === 'high' ? '✓ High Confidence' : '⚠ Suspicious / Low Confidence'}
                </span>
                <span className="bill-popup-reason">{billPopup.matchReason}</span>
              </div>

              <div className="bill-popup-grid">
                <div className="bill-popup-section">
                  <h4>Bill Information</h4>
                  <table className="bill-popup-table">
                    <tbody>
                      <tr><td>Flat</td><td><strong>{billPopup.blockName ? `${billPopup.blockName}-` : ''}{billPopup.flatNumber}</strong></td></tr>
                      <tr><td>Resident</td><td>{billPopup.residentName || '-'}</td></tr>
                      <tr><td>Category</td><td>{billPopup.billType ? <span className="recon-badge badge-blue">{billPopup.billType}</span> : '-'}</td></tr>
                      <tr><td>Bill Amount</td><td className="cell-amount"><strong>{fmt(billPopup.billAmount)}</strong></td></tr>
                      <tr><td>Billing Date</td><td className="cell-mono">{billPopup.billingDate}</td></tr>
                      <tr><td>Description</td><td style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatDescription(billPopup.remarks)}</td></tr>
                      <tr><td>Transaction ID</td><td className="cell-mono">{billPopup.billTransactionId || '-'}</td></tr>
                      <tr><td>Payment Mode</td><td>{billPopup.paymentMode ? <span className="recon-badge badge-blue">{billPopup.paymentMode}</span> : '-'}</td></tr>
                      <tr><td>Status</td><td>{approvedBills.has(billPopup.billId) ? <span className="recon-badge badge-green">Approved</span> : <span className="recon-badge badge-yellow">Pending</span>}</td></tr>
                    </tbody>
                  </table>
                </div>

                {billPopup._ledgerId !== 0 && (
                  <div className="bill-popup-section">
                    <h4>Bank Entry</h4>
                    <table className="bill-popup-table">
                      <tbody>
                        <tr><td>Date</td><td className="cell-mono">{billPopup.bankDate}</td></tr>
                        <tr><td>Particulars</td><td>{billPopup.bankParticulars || '-'}</td></tr>
                        <tr><td>Bank Amount</td><td className="cell-amount"><strong>{fmt(billPopup.bankAmount)}</strong></td></tr>
                        <tr><td>Bank Txn Ref</td><td className="cell-mono">{billPopup.bankTransactionId || '-'}</td></tr>
                        <tr><td>Bank Mode</td><td>{billPopup.bankPaymentMode ? <span className="recon-badge badge-blue">{billPopup.bankPaymentMode}</span> : '-'}</td></tr>
                        <tr>
                          <td>Amount Match</td>
                          <td>
                            {Math.abs(billPopup.billAmount - billPopup.bankAmount) < 0.01
                              ? <span className="recon-badge badge-green">✓ Exact Match</span>
                              : <span className="recon-badge badge-red">✗ Differs by {fmt(Math.abs(billPopup.billAmount - billPopup.bankAmount))}</span>
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {(billPopup.screenshotFilePath ||
                pendingGroups.some((g) => g.payment_ids.includes(billPopup.paymentId) && g.screenshot_file_path)) && (
                <div className="bill-popup-section screenshot-section" style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '10px' }}>Attached Screenshot</h4>
                  {popupScreenshotLoading ? (
                    <div className="screenshot-loading">Loading payment proof…</div>
                  ) : popupScreenshotUrl ? (
                    <div
                      className="screenshot-container"
                      style={{ cursor: 'pointer', position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}
                    >
                      <img
                        src={popupScreenshotUrl}
                        alt="Payment proof"
                        style={{ width: '100%', display: 'block', maxHeight: '300px', objectFit: 'contain', background: '#f8fafc' }}
                        onClick={() => window.open(popupScreenshotUrl, '_blank', 'noopener,noreferrer')}
                      />
                      <div className="screenshot-overlay">
                        <HiEye size={20} />
                        <span>View Full Size</span>
                      </div>
                    </div>
                  ) : (
                    <p className="screenshot-load-error">Could not load payment screenshot.</p>
                  )}
                </div>
              )}

              <div className="bill-popup-actions">
                {approvedBills.has(billPopup.billId) ? (
                  <>
                    <span className="recon-badge badge-green" style={{ fontSize: '0.85rem', padding: '6px 16px' }}>
                      <HiCheckCircle size={14} /> Reconciled
                    </span>
                    <button 
                      className="recon-btn recon-btn-outline recon-btn-sm btn-unlink"
                      style={{ color: '#dc2626', borderColor: '#fecaca' }}
                      onClick={() => { 
                        setConfirmModal({
                          title: 'Unlink Bill',
                          message: `Are you sure you want to remove this link?`,
                          onConfirm: () => {
                            handleUnlink(billPopup._ledgerId); 
                            setBillPopup(null);
                          },
                          type: 'danger'
                        });
                      }}
                      title="Unlink Bill"
                    >
                      <HiX size={14} />
                    </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                    {billPopup._ledgerId !== 0 && billPopup.matchConfidence !== 'high' && (
                      <div className="review-warning-box">
                        <HiExclamation size={18} />
                        <div>
                          <strong>Review Required:</strong> This match has {billPopup.matchConfidence} confidence. 
                          Please verify the details before approving.
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      {billPopup._ledgerId === 0 ? (
                        <>
                          <button 
                            className={`recon-btn ${isLinkingInPopup ? 'recon-btn-active' : 'recon-btn-outline'}`}
                            style={{ flex: 1 }}
                            onClick={() => setIsLinkingInPopup(!isLinkingInPopup)}
                          >
                            {isLinkingInPopup ? 'Cancel Linking' : 'Link To Transaction'}
                          </button>
                          {!isLinkingInPopup && (
                            <button 
                              className="recon-btn recon-btn-success" 
                              style={{ flex: 1 }}
                              onClick={() => {
                                setConfirmModal({
                                  title: 'Direct Approval',
                                  message: `Are you sure you want to mark Payment #${billPopup.billId} as PAID? This will create a new transaction in the society ledger.`,
                                  onConfirm: () => {
                                    handleApproveSingle(billPopup.billId);
                                    setBillPopup(null);
                                  },
                                  type: 'success'
                                });
                              }}
                            >
                              Approve
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          className={`recon-btn ${billPopup.matchConfidence === 'high' ? 'recon-btn-success' : 'recon-btn-warning'}`}
                          onClick={() => { 
                            setConfirmModal({
                              title: 'Final Verification',
                              message: `I have reviewed the details and confirm that this payment is valid for Payment #${billPopup.billId}. Proceed?`,
                              onConfirm: () => {
                                handleApproveSingle(billPopup.billId); 
                                setBillPopup(null);
                              },
                              type: 'success'
                            });
                          }}
                          disabled={approvingBills.has(billPopup.billId)}
                          style={{ flex: 1 }}
                        >
                          <HiCheckCircle size={16} />
                          {approvingBills.has(billPopup.billId) ? 'Approving...' : billPopup.matchConfidence === 'high' ? 'Approve This Bill' : 'Verify & Approve'}
                        </button>
                      )}
                      <button className="recon-btn recon-btn-outline" onClick={() => { setBillPopup(null); setIsLinkingInPopup(false); }} style={{ flex: billPopup._ledgerId === 0 ? 0.5 : 'initial' }}>Close</button>
                    </div>

                    {isLinkingInPopup && (
                      <div className="popup-linking-section" style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h4 style={{ margin: 0 }}>Select Bank Transaction</h4>
                          <div className="reconcile-search-box" style={{ width: '200px', margin: 0 }}>
                            <HiSearch size={14} />
                            <input 
                              type="text" 
                              placeholder="Search txn..." 
                              value={popupTxnSearch} 
                              onChange={e => setPopupTxnSearch(e.target.value)} 
                              style={{ fontSize: '0.8rem' }}
                            />
                          </div>
                        </div>
                        
                        <div className="popup-txn-list" style={{ maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {openTransactions
                            .filter(t => {
                              if (!popupTxnSearch) return true;
                              const q = popupTxnSearch.toLowerCase();
                              return (t.particulars || '').toLowerCase().includes(q) || 
                                     t.transactionRefs.some(r => r.toLowerCase().includes(q)) ||
                                     String(t.credit).includes(q);
                            })
                            .map(txn => (
                              <button 
                                key={txn.id} 
                                className="picker-bill-btn" 
                                style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                onClick={() => {
                                  const linkedGroup = pendingGroups.find((g) =>
                                    g.payment_ids.includes(billPopup.billId),
                                  );
                                  const bill =
                                    unmatchedRows.find((pb) => pb.paymentId === billPopup.billId) ??
                                    (linkedGroup ? groupToReconRow(linkedGroup) : undefined);
                                  if (bill) handleManualLink(txn.id, bill);
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{txn.particulars}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{txn.transactionDate} • {txn.transactionRefs[0]}</div>
                                </div>
                                <div style={{ fontWeight: 700, color: 'var(--tt-primary)' }}>{fmt(txn.credit || 0)}</div>
                              </button>
                            ))
                          }
                          {openTransactions.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '0.9rem' }}>
                              No open transactions available for linking.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Manual Link Modal ── */}
      {manualLinkEntry && (
        <div className="recon-modal-overlay" onClick={() => setManualLinkEntry(null)}>
          <div className="recon-modal manual-link-modal" onClick={e => e.stopPropagation()}>
            <div className="recon-modal-header">
              <div className="header-title">
                <HiDocumentText size={20} className="header-icon" />
                <div>
                  <h3>Manual Link</h3>
                  <p>Connect bank entry to an unpaid bill</p>
                </div>
              </div>
              <button className="recon-modal-close" onClick={() => setManualLinkEntry(null)}>&times;</button>
            </div>
            
            <div className="recon-modal-body">
              <div className="manual-link-context-box">
                <div className="context-row">
                  <div className="context-field">
                    <label>Bank Particulars</label>
                    <div className="context-value particulars">{manualLinkEntry.particulars || '-'}</div>
                  </div>
                  <div className="context-field amount-field">
                    <label>Amount</label>
                    <div className="context-value amount">{fmt(manualLinkEntry.credit || 0)}</div>
                  </div>
                </div>
                <div className="context-row">
                  <div className="context-field">
                    <label>Date</label>
                    <div className="context-value">{manualLinkEntry.transactionDate}</div>
                  </div>
                  <div className="context-field">
                    <label>Refs</label>
                    <div className="context-value refs">{manualLinkEntry.transactionRefs.join(', ') || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="modal-search-section">
                <div className="reconcile-search-box search-large">
                  <HiSearch size={18} />
                  <input 
                    type="text" 
                    placeholder="Search by flat, resident name, or bill amount..." 
                    value={manualBillSearch} 
                    onChange={e => setManualBillSearch(e.target.value)} 
                    autoFocus
                  />
                </div>
              </div>

              <div className="manual-picker-container">
                <div className="picker-header">
                  <span>Available Unmatched Bills ({unmatchedRows.length})</span>
                </div>
                <div className="manual-picker-list">
                  {unmatchedRows
                    .filter(b => {
                      if (!manualBillSearch) return true;
                      const q = manualBillSearch.toLowerCase();
                      return b.flatNumber.toLowerCase().includes(q) ||
                             String(b.paymentId).includes(q) ||
                             String(b.amount).includes(q) ||
                             (b.transactionId || '').toLowerCase().includes(q);
                    })
                    .slice(0, 50)
                    .map(bill => (
                      <button
                        key={bill.group_key}
                        className="picker-bill-btn"
                        onClick={() => handleManualLink(manualLinkEntry.id, bill)}
                      >
                        <div className="picker-bill-main">
                          <div className="picker-bill-id">
                            <span className="flat-badge">#{bill.paymentId} · {bill.blockName ? `${bill.blockName}-` : ''}{bill.flatNumber}</span>
                            {bill.paymentIds.length > 1 && (
                              <span className="resident-name">{bill.paymentIds.length} categories</span>
                            )}
                          </div>
                          <div className="picker-bill-date">{new Date(bill.paidAt).toLocaleDateString()}</div>
                        </div>
                        <div className="picker-bill-side">
                          <div className="bill-amt">{fmt(bill.amount)}</div>
                          <div className="select-hint">Click to Link</div>
                        </div>
                      </button>
                    ))
                  }
                  {unmatchedRows.length === 0 && (
                    <div className="empty-picker">
                      <HiExclamation size={24} />
                      <p>No unmatched bills available in this period.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="recon-modal-footer">
              <button className="recon-btn recon-btn-outline" onClick={() => setManualLinkEntry(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Confirmation Modal ── */}
      {confirmModal && (
        <div className="recon-modal-overlay confirmation-overlay" style={{ zIndex: 3000 }}>
          <div className="recon-modal confirmation-modal" onClick={e => e.stopPropagation()}>
            <div className={`confirmation-header ${confirmModal.type || 'warning'}`}>
              <HiExclamation size={24} />
              <h3>{confirmModal.title}</h3>
            </div>
            <div className="confirmation-body">
              <p style={{ whiteSpace: 'pre-wrap' }}>{confirmModal.message}</p>
            </div>
            <div className="confirmation-footer">
              <button className="recon-btn recon-btn-outline" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button 
                className={`recon-btn ${
                  confirmModal.type === 'danger' ? 'recon-btn-danger' : 
                  confirmModal.type === 'success' ? 'recon-btn-success' : 
                  'recon-btn-warning'
                }`}
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal(null);
                }}
              >
                {confirmModal.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminReconcile;
